import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  UsersIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  CheckIcon,
  XIcon,
  RefreshCwIcon,
  SaveIcon,
  GripVerticalIcon,
  SearchIcon,
  FilterIcon
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable Item Component - MUST be outside main component to prevent re-renders
const SortableUserTypeItem = ({ 
  userType, 
  editingId, 
  editTypeName, 
  setEditTypeName, 
  saveEdit, 
  cancelEdit, 
  startEdit, 
  openDeleteModal,
  saving 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: userType.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg transition-all ${
        isDragging ? 'shadow-xl bg-white border-2 border-purple-400 z-50' : 'hover:bg-gray-100'
      }`}
    >
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        {/* Drag Handle */}
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
          <GripVerticalIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
        </div>

        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
          <UsersIcon className="w-4 h-4 text-purple-600" />
        </div>

        <div className="flex-1 min-w-0">
          {editingId === userType.id ? (
            <Input
              value={editTypeName}
              onChange={(e) => setEditTypeName(e.target.value)}
              className="w-full text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveEdit(userType.id);
                } else if (e.key === 'Escape') {
                  cancelEdit();
                }
              }}
            />
          ) : (
            <div className="min-w-0">
              <h3 className="font-medium text-sm text-gray-900 truncate">
                {userType.name}
              </h3>
              <p className="text-xs text-gray-500 truncate">
                ID: {userType.id.slice(-8)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
        {editingId === userType.id ? (
          <>
            <Button
              size="sm"
              onClick={() => saveEdit(userType.id)}
              disabled={saving}
              className="gap-1 h-7 px-2"
            >
              <CheckIcon className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={cancelEdit}
              disabled={saving}
              className="gap-1 h-7 px-2"
            >
              <XIcon className="w-3 h-3" />
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => startEdit(userType)}
              className="gap-1 h-7 px-2"
            >
              <EditIcon className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openDeleteModal(userType)}
              className="gap-1 h-7 px-2 text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
            >
              <TrashIcon className="w-3 h-3" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const UserTypesSettings = () => {
  const [userTypes, setUserTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Add new type state
  const [newTypeName, setNewTypeName] = useState('');
  const [addingNew, setAddingNew] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editTypeName, setEditTypeName] = useState('');

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  // Capitalize first letter of each word
  const capitalizeFirstLetter = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Fetch user types (roles)
  const fetchUserTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/roles`);
      // Sort by display_order if exists, otherwise keep original order
      const sorted = response.data.sort((a, b) => {
        return (a.display_order || 0) - (b.display_order || 0);
      });
      setUserTypes(sorted);
    } catch (error) {
      console.error('Error fetching user types:', error);
      toast.error('Kullanıcı tipleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserTypes();
  }, []);

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = userTypes.findIndex((item) => item.id === active.id);
    const newIndex = userTypes.findIndex((item) => item.id === over.id);

    const newItems = arrayMove(userTypes, oldIndex, newIndex);

    // Update local state immediately
    setUserTypes(newItems);

    // Update display_order on backend
    try {
      const updatePromises = newItems.map((item, index) => 
        axios.put(`${API}/roles/${item.id}`, {
          ...item,
          display_order: index
        })
      );
      await Promise.all(updatePromises);
      toast.success('Sıralama kaydedildi');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Sıralama kaydedilemedi');
      // Revert on error
      fetchUserTypes();
    }
  };

  // Toggle Super Admin status
  // Add new user type
  const addUserType = async () => {
    if (!newTypeName.trim()) {
      toast.error('Lütfen pozisyon adı girin');
      return;
    }

    // Capitalize the name
    const formattedName = capitalizeFirstLetter(newTypeName.trim());

    // Check if type already exists
    const existingType = userTypes.find(type => 
      type.name.toLowerCase() === formattedName.toLowerCase()
    );
    
    if (existingType) {
      toast.error('Bu kullanıcı tipi zaten mevcut');
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(`${API}/roles`, {
        name: formattedName,
        description: `${formattedName} kullanıcı tipi`
      });

      setUserTypes([...userTypes, response.data]);
      setNewTypeName('');
      setAddingNew(false);
      toast.success('Kullanıcı tipi eklendi');
    } catch (error) {
      console.error('Error adding user type:', error);
      toast.error('Kullanıcı tipi eklenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // Start editing
  const startEdit = (userType) => {
    setEditingId(userType.id);
    setEditTypeName(userType.name);
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditTypeName('');
  };

  // Save edit
  const saveEdit = async (typeId) => {
    if (!editTypeName.trim()) {
      toast.error('Lütfen kullanıcı tipi adı girin');
      return;
    }

    // Capitalize the name
    const formattedName = capitalizeFirstLetter(editTypeName.trim());

    // Check if name already exists (excluding current item)
    const existingType = userTypes.find(type => 
      type.id !== typeId && 
      type.name.toLowerCase() === formattedName.toLowerCase()
    );
    
    if (existingType) {
      toast.error('Bu kullanıcı tipi adı zaten kullanılıyor');
      return;
    }

    try {
      setSaving(true);
      await axios.put(`${API}/roles/${typeId}`, {
        name: formattedName,
        description: `${formattedName} kullanıcı tipi`
      });

      // Update local state
      setUserTypes(prev => prev.map(type => 
        type.id === typeId 
          ? { ...type, name: formattedName }
          : type
      ));

      setEditingId(null);
      setEditTypeName('');
      toast.success('Kullanıcı tipi güncellendi');
    } catch (error) {
      console.error('Error updating user type:', error);
      toast.error('Kullanıcı tipi güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (userType) => {
    setTypeToDelete(userType);
    setDeleteModalOpen(true);
    setDeleteSuccess(false);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setTypeToDelete(null);
    setDeleteSuccess(false);
  };

  // Delete user type
  const confirmDeleteUserType = async () => {
    if (!typeToDelete) return;

    try {
      setSaving(true);
      await axios.delete(`${API}/roles/${typeToDelete.id}`);
      
      setUserTypes(prev => prev.filter(type => type.id !== typeToDelete.id));
      setDeleteSuccess(true);
      
      // Close modal after 2 seconds
      setTimeout(() => {
        closeDeleteModal();
      }, 2000);
    } catch (error) {
      console.error('Error deleting user type:', error);
      if (error.response?.status === 400) {
        toast.error('Bu kullanıcı tipi aktif kullanıcılar tarafından kullanıldığı için silinemez');
      } else {
        toast.error('Kullanıcı tipi silinirken hata oluştu');
      }
    } finally {
      setSaving(false);
    }
  };

  // Get user count for each type
  const getUserCount = async (typeId) => {
    try {
      const response = await axios.get(`${API}/roles/${typeId}/users`);
      return response.data.length;
    } catch (error) {
      return 0;
    }
  };

  // Filter user types based on search query
  const filteredUserTypes = userTypes.filter(userType => {
    if (!searchQuery.trim()) return true;
    return userType.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <RefreshCwIcon className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Kullanıcı tipleri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <UsersIcon className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kullanıcı Pozisyonları</h1>
            <p className="text-gray-600">
              Sistem kullanıcı pozisyonlarını yönetin (CEO, Müdür, Uzman vb.)
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Listede olmayan pozisyonları ekleyebilir yada silebilirsiniz
            </p>
          </div>
        </div>

        <div className="flex space-x-2 ml-4">
          <Button 
            onClick={() => setAddingNew(true)} 
            className="gap-2"
            disabled={addingNew}
          >
            <PlusIcon className="w-4 h-4" />
            Yeni Pozisyon Ekle
          </Button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <div className="relative w-1/2">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Pozisyon ara... (örn: Müdür, CEO, Uzman)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
            <FilterIcon className="w-4 h-4" />
            <span>
              {filteredUserTypes.length} / {userTypes.length} sonuç
            </span>
          </div>
        </div>
      </Card>

      {/* Add New Type */}
      {addingNew && (
        <Card className="p-4 border-green-200 bg-green-50">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Pozisyon adını girin (örn: Genel Müdür)"
                className="bg-white"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addUserType();
                  } else if (e.key === 'Escape') {
                    setAddingNew(false);
                    setNewTypeName('');
                  }
                }}
              />
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={addUserType}
                disabled={saving}
                className="gap-1"
              >
                <CheckIcon className="w-4 h-4" />
                Ekle
              </Button>
              <Button 
                onClick={() => {
                  setAddingNew(false);
                  setNewTypeName('');
                }}
                variant="outline"
                disabled={saving}
                className="gap-1"
              >
                <XIcon className="w-4 h-4" />
                İptal
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* User Types List */}
      <Card>
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UsersIcon className="w-5 h-5" />
            Mevcut Pozisyonlar ({userTypes.length})
          </h2>
        </div>
        
        <div className="p-6">
          {userTypes.length === 0 ? (
            <div className="text-center py-12">
              <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Henüz pozisyon tanımlanmamış
              </h3>
              <p className="text-gray-600">
                "Yeni Pozisyon Ekle" butonu ile pozisyonları ekleyin
              </p>
            </div>
          ) : filteredUserTypes.length === 0 ? (
            <div className="text-center py-12">
              <SearchIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Sonuç bulunamadı
              </h3>
              <p className="text-gray-600">
                "<strong>{searchQuery}</strong>" için eşleşen pozisyon bulunamadı
              </p>
              <Button 
                onClick={() => setSearchQuery('')}
                variant="outline"
                className="mt-4"
              >
                Aramayı Temizle
              </Button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredUserTypes.map((item) => item.id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-2 gap-4">
                  {filteredUserTypes.map((userType) => (
                    <SortableUserTypeItem 
                      key={userType.id} 
                      userType={userType}
                      editingId={editingId}
                      editTypeName={editTypeName}
                      setEditTypeName={setEditTypeName}
                      saveEdit={saveEdit}
                      cancelEdit={cancelEdit}
                      startEdit={startEdit}
                      openDeleteModal={openDeleteModal}
                      saving={saving}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </Card>

      {/* Info */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
          <UsersIcon className="w-4 h-4 mr-2" />
          Bilgilendirme
        </h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>🔍 Arama:</strong> Pozisyonları hızlıca bulmak için arama kutusunu kullanın</li>
          <li>• <strong>Yeni Pozisyon:</strong> Sisteme yeni pozisyonlar ekleyebilirsiniz</li>
          <li>• <strong>Düzenleme:</strong> Mevcut pozisyonların adlarını değiştirebilirsiniz</li>
          <li>• <strong>Silme:</strong> Aktif kullanıcı tarafından kullanılmayan pozisyonlar silinebilir</li>
          <li>• <strong>Sıralama:</strong> Kartları sürükleyip bırakarak sıralayabilirsiniz (drag & drop)</li>
          <li>• <strong>Hiyerarşi:</strong> Bu pozisyonlar kullanıcı yönetiminde kullanılır</li>
        </ul>
      </Card>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {deleteSuccess ? (
              /* Success State */
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckIcon className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Başarılı!
                </h3>
                <p className="text-gray-600">
                  <strong>{typeToDelete?.name}</strong> kullanıcı tipi başarıyla silindi.
                </p>
              </div>
            ) : (
              /* Confirmation State */
              <>
                <div className="p-6">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrashIcon className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 text-center mb-4">
                    Kullanıcı Tipini Sil
                  </h3>
                  <p className="text-gray-600 text-center mb-2">
                    <strong className="text-gray-900">{typeToDelete?.name}</strong> kullanıcı tipini silmek istediğinizden emin misiniz?
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Dikkat:</strong> Bu tip kullanıcı tarafından kullanılıyorsa silinemeyecektir.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-lg">
                  <Button
                    onClick={closeDeleteModal}
                    variant="outline"
                    disabled={saving}
                    className="gap-1"
                  >
                    <XIcon className="w-4 h-4" />
                    İptal
                  </Button>
                  <Button
                    onClick={confirmDeleteUserType}
                    disabled={saving}
                    className="gap-1 bg-red-600 hover:bg-red-700"
                  >
                    {saving ? (
                      <>
                        <RefreshCwIcon className="w-4 h-4 animate-spin" />
                        Siliniyor...
                      </>
                    ) : (
                      <>
                        <TrashIcon className="w-4 h-4" />
                        Sil
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTypesSettings;